type EntityMapper<T = any> = (
  raw: T,
  staticMapperValue?: Record<string, any>,
) => { id: number; label: string };

type MapperKeys =
  | "from_city"
  | "to_city"
  | "loading"
  | "truck_kind"
  | "discharge"
  | "shipping_case"
  | "items"
  | "ddt_number"
  | "mark"
  | "reference_order"
  | "vessel"
  | "truck_plate"
  | "loading_ddt"
  | "deposit_reference"
  | "destination"
  | "comment"
  | "special_instruction"
  | "commodities"
  | "ports"
  | "container"
  | "customers"
  | "id_number"
  | "customs_offices"
  | "bonded_warehouse"
  | "guarantee_office"
  | "suppliers_to_broker"
  | "d1_intro_customer";

type Mappers = Record<MapperKeys, EntityMapper>;

export type OptionMapper = (typeof mappers)[keyof typeof mappers];

const mappers: Mappers = {
  from_city: (raw) => ({
    id: raw.city?.id,
    label: raw.city?.name || raw.name,
    rowId: raw.id,
  }),
  to_city: (raw) => ({
    id: raw.delivery_city?.id,
    label: raw.delivery_city?.name || raw.name,
    rowId: raw.id,
  }),
  loading: (raw) => ({
    id: raw.id,
    label: raw.title || raw.name,
    opening_hours: raw.opening_hours,
  }),
  discharge: (raw) => ({
    id: raw.id,
    label: raw.destination,
    discharging_time: raw.discharging_time,
    special_instruction: raw.special_instruction,
  }),
  shipping_case: (raw) => ({
    id: raw.id,
    label: raw.title,
  }),
  truck_kind: (raw) => ({
    id: raw.id,
    label: raw.name_it,
  }),
  items: (raw) => ({
    id: raw.id,
    label: raw.id_number,
  }),
  ddt_number: (raw) => ({
    id: raw.id,
    label: String(raw.value),
  }),
  mark: (raw) => ({
    id: raw.id,
    label: raw.value,
  }),
  reference_order: (raw) => ({
    id: raw.id,
    label: raw.value,
  }),
  vessel: (raw) => ({
    id: raw.id,
    label: raw.value,
  }),
  truck_plate: (raw) => ({
    id: raw.id,
    label: raw.value,
  }),
  loading_ddt: (raw) => ({
    id: raw.id,
    label: String(raw.value),
  }),
  deposit_reference: (raw) => ({
    id: raw.id,
    label: raw.value,
  }),
  destination: (raw) => ({
    id: raw.id,
    label: raw.destination,
  }),
  comment: (raw) => ({
    id: raw.id,
    label: raw.value,
  }),
  special_instruction: (raw) => ({
    id: raw.id,
    label: raw.value,
  }),
  commodities: (raw) => ({
    id: raw.id,
    label: raw.name_it,
  }),
  ports: (raw) => ({
    id: raw.id,
    label: raw.title,
  }),
  customers: (raw) => ({
    id: raw.id,
    label: raw.name,
    terms_of_payment: raw.terms_of_payment,
  }),
  id_number: (raw) => ({
    id: raw.id,
    label: raw.id_number,
  }),
  customs_offices: (raw) => ({
    id: raw.id,
    label: raw.title,
  }),
  bonded_warehouse: (raw) => ({
    id: raw.id,
    label: raw.name,
    bonded_warehouse_code: raw.bonded_warehouse_code ?? null,
    customs_decision_h2: raw.customs_decision_h2 ?? null,
    authorization_number_h2: raw.authorization_number_h2 ?? null,
    guarantee_a11_ter_h2: raw.guarantee_a11_ter_h2 ?? null,
  }),
  guarantee_office: (raw) => ({
    id: raw.id,
    label: raw.fiscal_warehouse_a11_ter,
  }),
  suppliers_to_broker: (raw) => ({
    id: raw.id,
    label: raw.name,
    depositor_broker: raw.name,
  }),
  container: (raw) => ({
    id: raw.id,
    label: raw.value,
  }),
  d1_intro_customer: (raw, staticMapperValue) => ({
    id: raw.id,
    label: `${staticMapperValue?.offer_customer_name || ""} - ${raw.name}`,
  }),
};

export function getMapper(key: string): EntityMapper {
  return (
    (mappers as Partial<Record<string, EntityMapper>>)[key] ??
    ((raw) => ({ id: raw.id, label: raw.name }))
  );
}

type Value =
  | { id: number; name: string }
  | { id: number; name: string }[]
  | null;

export const mapToOption = (value: Value) => {
  if (!value) return null;

  if (Array.isArray(value)) {
    return value.map((v) => ({ id: v.id, label: v.name }));
  }

  return { id: value.id, label: value.name };
};

export const mapFieldToOption = (fieldName: string, value: any) => {
  if (!value) return null;
  const mapper = mappers[fieldName as keyof typeof mappers];
  if (mapper) {
    const mapped = mapper(value);
    return mapped ? { id: mapped.id, label: mapped.label } : null;
  }
  return mapToOption(value);
};

type OptionType = { id: string | number | boolean; label: string };

export const mapFieldsToOptions = (
  fieldName: string,
  values: any[] | null,
): OptionType[] => {
  if (!Array.isArray(values) || values.length === 0) return [];

  const mapper = mappers[fieldName as keyof typeof mappers];

  return values
    .flatMap((value) => {
      if (!value) return [];
      const mapped = mapper ? mapper(value) : mapToOption(value);

      if (Array.isArray(mapped)) {
        return mapped
          .filter(Boolean)
          .map((m) => ({ id: m.id, label: m.label })) as OptionType[];
      }

      if (mapped && typeof mapped === "object") {
        return [{ id: mapped.id, label: mapped.label }] as OptionType[];
      }

      return [];
    })
    .filter(Boolean);
};
